import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import { nip19 } from 'nostr-tools';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Calendar, Zap, User, Copy, Check } from 'lucide-react';
import { useNostrLogin } from '@nostrify/react/login';
import { useState } from 'react';
import { Navbar } from '@/components/Navbar';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { SIMPLY_NOSTR_PUBKEY } from '@/hooks/useEpisodes';
import { useAuthor } from '@/hooks/useAuthor';
import type { NostrEvent } from '@nostrify/nostrify';

const EPISODE_RELAYS = [
  'wss://relay.damus.io',
  'wss://relay.primal.net',
  'wss://nos.lol',
  'wss://relay.nostr.band',
  'wss://relay.ditto.pub',
];

interface EpisodePageProps {
  eventId: string;
}

function extractVideoUrl(content: string): string | null {
  const urlRegex = /https?:\/\/[^\s\n]+/g;
  const urls = content.match(urlRegex) || [];
  for (const rawUrl of urls) {
    const url = rawUrl.replace(/[).,!?]+$/, '');
    if (
      url.includes('cdn.satellite.earth') ||
      url.includes('satellite.earth') ||
      url.includes('v.nostr.build') ||
      url.match(/\.(mp4|webm|mov|MP4|MOV)(\?|$)/)
    ) {
      return url;
    }
  }
  return null;
}

function extractEpisodeNumber(content: string): number | null {
  const patterns = [/[Ee]p\.?\s*(\d+)/, /[Ee]pisode\s*(\d+)/, /#(\d{1,3})(?:\s|$)/];
  for (const pattern of patterns) {
    const m = content.match(pattern);
    if (m) {
      const n = parseInt(m[1], 10);
      if (n >= 1 && n <= 200) return n;
    }
  }
  return null;
}

function extractGuestPubkey(event: NostrEvent): string | null {
  const pTag = event.tags.find(([t]) => t === 'p');
  return pTag?.[1] ?? null;
}

function formatDate(ts: number): string {
  return new Date(ts * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function cleanContentForDisplay(content: string): string {
  return content
    .split('\n')
    .filter((line) => {
      const t = line.trim();
      if (!t) return true;
      if (!t.match(/^https?:\/\//)) return true;
      // Remove video URLs
      if (
        t.includes('cdn.satellite.earth') ||
        t.includes('satellite.earth') ||
        t.match(/\.(mp4|webm|mov|MP4|MOV)/)
      ) {
        return false;
      }
      return true;
    })
    .join('\n')
    .trim();
}

const LIGHTNING_ADDRESS = 'simplynostr@wallet.yakihonne.com';

function ZapStrip() {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(LIGHTNING_ADDRESS);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex items-center gap-3 p-4 rounded-xl border border-yellow-500/20 bg-yellow-500/5">
      <Zap className="w-4 h-4 text-yellow-400 shrink-0" />
      <span className="text-sm text-muted-foreground flex-1">
        Enjoyed this episode?{' '}
        <a
          href={`lightning:${LIGHTNING_ADDRESS}`}
          className="text-yellow-400 hover:text-yellow-300 font-medium transition-colors"
        >
          Send a zap ⚡
        </a>
      </span>
      <button
        onClick={handleCopy}
        title="Copy lightning address"
        className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
      >
        {copied
          ? <Check className="w-4 h-4 text-green-400" />
          : <Copy className="w-4 h-4" />
        }
      </button>
    </div>
  );
}

function GuestCard({ pubkey }: { pubkey: string }) {
  const author = useAuthor(pubkey);
  const meta = author.data?.metadata;
  const npub = nip19.npubEncode(pubkey);

  if (author.isLoading) {
    return (
      <div className="flex items-center gap-3 p-4 rounded-xl border border-border/50 bg-muted/30">
        <Skeleton className="w-12 h-12 rounded-full" />
        <div className="space-y-1.5">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-3 w-40" />
        </div>
      </div>
    );
  }

  return (
    <a
      href={`https://njump.me/${npub}`}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 p-4 rounded-xl border border-border/50 bg-muted/30 hover:border-purple-500/50 hover:bg-purple-500/5 transition-all group"
    >
      <div className="w-12 h-12 rounded-full overflow-hidden bg-purple-900 shrink-0">
        {meta?.picture ? (
          <img src={meta.picture} alt={meta.name ?? 'Guest'} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <User className="w-5 h-5 text-purple-400" />
          </div>
        )}
      </div>
      <div className="min-w-0">
        <p className="font-semibold text-sm text-foreground group-hover:text-purple-300 transition-colors truncate">
          {meta?.display_name || meta?.name || npub.slice(0, 12) + '…'}
        </p>
        {meta?.about && (
          <p className="text-xs text-muted-foreground truncate mt-0.5">{meta.about}</p>
        )}
      </div>
      <ExternalLink className="w-3.5 h-3.5 text-muted-foreground ml-auto shrink-0" />
    </a>
  );
}

export function EpisodePage({ eventId }: EpisodePageProps) {
  const { nostr } = useNostr();
  const { logins } = useNostrLogin();
  const navigate = useNavigate();

  useEffect(() => {
    if (logins.length === 0) navigate('/', { replace: true });
  }, [logins, navigate]);

  const EPISODE_RELAYS = [
    'wss://relay.damus.io',
    'wss://relay.primal.net',
    'wss://nos.lol',
    'wss://relay.nostr.band',
    'wss://relay.ditto.pub',
  ];

  const { data: event, isLoading, isError } = useQuery({
    queryKey: ['episode', eventId],
    queryFn: async (c) => {
      const relayGroup = nostr.group(EPISODE_RELAYS);
      const events = await relayGroup.query(
        [{ ids: [eventId], authors: [SIMPLY_NOSTR_PUBKEY], kinds: [1] }],
        { signal: c.signal }
      );
      return events[0] ?? null;
    },
  });

  const guestPubkey = event ? extractGuestPubkey(event) : null;
  const videoUrl = event ? extractVideoUrl(event.content) : null;
  const episodeNumber = event ? extractEpisodeNumber(event.content) : null;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-6">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-10 w-3/4" />
          <Skeleton className="aspect-video w-full rounded-xl" />
          <div className="space-y-3">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-4/5" />
          </div>
        </main>
      </div>
    );
  }

  if (isError || !event) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="max-w-3xl mx-auto px-4 sm:px-6 py-20 text-center">
          <p className="text-muted-foreground">Episode not found.</p>
          <Link to="/episodes" className="mt-4 inline-block text-purple-400 hover:text-purple-300 text-sm">
            ← Back to episodes
          </Link>
        </main>
      </div>
    );
  }

  const cleanContent = cleanContentForDisplay(event.content);
  const lines = cleanContent.split('\n').filter(Boolean);
  const title = lines[0]
    ? lines[0].length <= 120 ? lines[0] : lines[0].substring(0, 100) + '…'
    : episodeNumber ? `Simply Nostr — Episode ${episodeNumber}` : 'Simply Nostr Episode';
  const body = lines.slice(1).join('\n').trim();

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 pb-20 space-y-8">
        {/* Back link */}
        <Link
          to="/episodes"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          All Episodes
        </Link>

        {/* Title */}
        <div className="space-y-3">
          <h1 className="text-2xl sm:text-3xl font-black leading-tight text-foreground">{title}</h1>
          <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              {formatDate(event.created_at)}
            </span>
            {episodeNumber && (
              <Badge className="bg-purple-600/80 text-white border-0 text-xs">
                Episode {episodeNumber}
              </Badge>
            )}
          </div>
        </div>

        {/* Video — open on satellite.earth */}
        {videoUrl && (
          <a
            href={videoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block group relative aspect-video rounded-xl overflow-hidden border border-border/50 bg-black"
          >
            {/* Dark gradient play screen */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-950 to-black flex flex-col items-center justify-center gap-4">
              <div className="w-20 h-20 rounded-full bg-purple-600 group-hover:bg-purple-500 flex items-center justify-center shadow-2xl shadow-purple-500/40 transition-all group-hover:scale-110">
                <svg className="w-8 h-8 text-white ml-1" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
              <p className="text-white/70 text-sm font-medium group-hover:text-white transition-colors">
                Watch on Satellite.earth
              </p>
            </div>
          </a>
        )}

        {/* Guest */}
        {guestPubkey && (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Guest</p>
            <GuestCard pubkey={guestPubkey} />
          </div>
        )}

        {/* Episode notes */}
        {body && (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Episode Notes</p>
            <Card className="border-border/50">
              <CardContent className="p-5">
                <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap">{body}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Support / Zap strip */}
        <ZapStrip />

        {/* Nostr link */}
        <div className="pt-2">
          <Button variant="outline" size="sm" asChild>
            <a
              href={`https://njump.me/${nip19.noteEncode(event.id)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2"
            >
              <Zap className="w-3.5 h-3.5 text-yellow-400" />
              View on Nostr
            </a>
          </Button>
        </div>
      </main>
    </div>
  );
}
